// @ts-check
'use strict';

/**
 * Record ids worked out from what a record belongs to, the same on every
 * device and on the server: a job's visit, a visit's area, an area's rating of
 * one surface, a job's centre. Two iPads that each start a job's condition
 * report — even offline — make the SAME records, which CRM merges (a create of
 * an existing id is an update), instead of two visits or two ratings of one
 * wall (PR #457 review).
 *
 * The ids are name-based (version 5) UUIDs, RFC 4122 §4.3. SHA-1 is written
 * out here because a browser only offers it asynchronously (crypto.subtle)
 * and the app makes these ids while saving.
 */

/** The namespace of every condition record id. Never change it: every job would get second records. */
const CONDITION_NAMESPACE = '3f1c6c2e-8a4b-5d7e-9b21-6e0f4a7c2d59';

/** SHA-1 of bytes (FIPS 180-4). Pure. @param {Uint8Array} bytes @returns {Uint8Array} */
function sha1(bytes) {
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array((((bytes.length + 8) >> 6) + 1) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 4, bitLength >>> 0);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
  let h0 = 0x67452301, h1 = 0xefcdab89, h2 = 0x98badcfe, h3 = 0x10325476, h4 = 0xc3d2e1f0;
  const w = new Uint32Array(80);
  for (let block = 0; block < padded.length; block += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(block + i * 4);
    for (let i = 16; i < 80; i++) {
      const x = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
      w[i] = (x << 1) | (x >>> 31);
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4;
    for (let i = 0; i < 80; i++) {
      const [f, k] = i < 20 ? [(b & c) | (~b & d), 0x5a827999] : i < 40 ? [b ^ c ^ d, 0x6ed9eba1] : i < 60 ? [(b & c) | (b & d) | (c & d), 0x8f1bbcdc] : [b ^ c ^ d, 0xca62c1d6];
      const t = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = (b << 30) | (b >>> 2);
      b = a;
      a = t;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }
  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  [h0, h1, h2, h3, h4].forEach((h, i) => outView.setUint32(i * 4, h));
  return out;
}

/** @param {string} uuid */
const uuidBytes = (uuid) => Uint8Array.from((uuid.replace(/-/g, '').match(/../g) || []).map((h) => parseInt(h, 16)));

/**
 * A name-based (version 5) UUID: the same name in the same namespace always
 * gives the same id. Pure. @param {string} name @param {string} namespace
 */
function uuidV5(name, namespace) {
  const ns = uuidBytes(namespace);
  const nameBytes = new TextEncoder().encode(name);
  const input = new Uint8Array(ns.length + nameBytes.length);
  input.set(ns);
  input.set(nameBytes, ns.length);
  const hash = sha1(input).slice(0, 16);
  hash[6] = (hash[6] & 0x0f) | 0x50;
  hash[8] = (hash[8] & 0x3f) | 0x80;
  const hex = Array.from(hash, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * `registerbuilding` / `registerarea`: a building's or area's entry in the Field Service register (a
 * functional location under the job's centre), made once the job is won.
 * @typedef {'centre' | 'visit' | 'visitarea' | 'observation' | 'areatag' | 'visitareatag' | 'registerbuilding' | 'registerarea'} ConditionRecordKind
 */

/**
 * The id of a condition record, from the ids of what it belongs to:
 * centre and visit — the job (opportunity); visitarea — the visit and the
 * job's area; observation — the visit area and the surface type, or the
 * measured surface's own id for a surface named on the job (Measure2Quote
 * condition-lines.ts, #468); areatag and visitareatag — the area (or visit
 * area) and the tick's name (the tag's key, or a typed entry's text;
 * @higgins/m2q-pricing tickWrites). Case doesn't matter.
 *
 * @param {ConditionRecordKind} kind
 * @param {...string} parts
 */
function conditionRecordId(kind, ...parts) {
  return uuidV5([kind, ...parts.map((p) => p.toLowerCase())].join('/'), CONDITION_NAMESPACE);
}

/**
 * The namespace of every Measure2Quote REVIEW answer's id. Never change it:
 * every version would get second rows. migrate-review-answers.ps1 holds the same.
 */
const REVIEW_ANSWER_NAMESPACE = '4b19b95c-e335-40a5-9c0d-d0b4ff1aa215';

/**
 * A REVIEW answer row's id (hig_reviewanswer), from its version's id and the
 * question's key: two iPads answering the same question, the migration and a
 * new version's copy all make the same row. Case of the GUID doesn't matter.
 *
 * @param {string} versionId
 * @param {string} questionKey
 */
function reviewAnswerId(versionId, questionKey) {
  return uuidV5(`reviewanswer/${versionId.toLowerCase()}/${questionKey}`, REVIEW_ANSWER_NAMESPACE);
}

module.exports = { conditionRecordId, reviewAnswerId, uuidV5 };
